import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { DropdownItem } from '@atlaskit/dropdown-menu';

import {
	type MenuItemReorderOperation,
	OPERATIONS,
	type OrderPreference,
} from '../../utils/dnd-data';
import { calculateNestedItemPosition } from '../../utils/nested-item-reordering/calculateNestedItemPosition';
import { useUpdateNestedItemOrder } from '../../utils/nested-item-reordering/useUpdateNestedItemOrder';
import type { AnalyticsEventHandler } from '../../utils/use-item-drop';

import { NavItemReorderingMenu } from './nav-item-reordering';

interface NestedItemsActionsAdapterProps<T> {
	currentItems: T[];
	currentOrder: OrderPreference;
	entityAri: string;
	fireAnalyticsEvent?: AnalyticsEventHandler;
	getItemId: (item: T) => string;
	item: { id: string; label: string };
	ownerAri: string;
}

/**
 * Adapter component that bridges navigation-specific reordering logic
 * with the reusable NavItemReorderingMenu component, but with custom trigger styling
 */
export function NestedItemsActionsAdapter({
	currentItems,
	currentOrder,
	entityAri,
	fireAnalyticsEvent,
	item,
	ownerAri,
	getItemId,
}: NestedItemsActionsAdapterProps<unknown>): React.JSX.Element | null {
	useIntl();

	// Platform mutation hook for reordering
	const updateSidebarItemOrder = useUpdateNestedItemOrder();

	// Calculate position capabilities using extracted utility
	const { canMoveUp, canMoveDown } = useMemo(
		() => calculateNestedItemPosition({ itemId: item.id, currentItems, currentOrder, getItemId }),
		[currentItems, currentOrder, getItemId, item.id],
	);

	// Handle move operations
	const handleMove = useCallback(
		(operation: MenuItemReorderOperation) => {
			updateSidebarItemOrder({
				currentItems: currentItems,
				currentOrder: currentOrder,
				entityAri,
				fireAnalyticsEvent,
				operation,
				ownerAri,
				targetItemId: item.id,
				getItemId,
			});
		},
		[
			updateSidebarItemOrder,
			currentItems,
			currentOrder,
			entityAri,
			fireAnalyticsEvent,
			ownerAri,
			item.id,
			getItemId,
		],
	);

	// If there's nothing to reorder, don't render anything
	if (!canMoveUp && !canMoveDown) {
		return null;
	}

	return (
		<NavItemReorderingMenu
			TriggerComponent={DropdownItem}
			canMoveUp={canMoveUp}
			canMoveDown={canMoveDown}
			moveUp={() => handleMove(OPERATIONS.MOVE_UP)}
			moveDown={() => handleMove(OPERATIONS.MOVE_DOWN)}
			moveToTop={() => handleMove(OPERATIONS.MOVE_TO_TOP)}
			moveToBottom={() => handleMove(OPERATIONS.MOVE_TO_BOTTOM)}
		/>
	);
}
